import { useState, useEffect } from 'react';
import { uniqueId, type LoggerInterface, type AppState } from '@nextgen-web-framework/core';
import { type NotificationTheme, Theme, useToast, useTranslation } from '@nextgen-web-framework/all';
import { showToastMsg } from '../utils/utils';

type TToastParams = Partial<Omit<Parameters<typeof showToastMsg>[0], 'addToast' | 'deleteToast'>>;
interface IHandleExceptionOptions {
  /**
   * logger service instance method
   */
  logServiceInstanceMethod?: TLogMethodType<LoggerInterface>;
  /**
   * identifier, such as to print along with logs
   */
  identifier?: string;
  /**
   * options to show toast or alert, check the types for more details
   */
  toast?: {
    enabled?: boolean;
    fallbackMessage?: string;
    params?: TToastParams;
  };
}

type TLogMethodType<T> = {
  [P in keyof T]: T[P];
}[keyof T];

/**
 * Hook to handle exception with options as logger
 *
 * You can pass default option, as well as override the options when calling handleException and any future methods to be implemented.
 * @param logServiceInstanceMethod - logger service instance method, e.g. log, error etc.
 * @param defaultOptions - options to control exception handling, such as toast message control
 * @returns
 */
export default function useHandleException(defaultOptions?: IHandleExceptionOptions) {
  const t = useTranslation();
  const [exceptionState, setExceptionState] = useState<{
    error?: AppState;
    options?: Pick<IHandleExceptionOptions, 'toast' | 'logServiceInstanceMethod' | 'identifier'>;
  }>({});
  const { addToast, deleteToast } = useToast();

  const errorCode = `subscription.errors.${exceptionState?.error?.responseBody?.code}`;
  let translatedError = t(errorCode);
  translatedError = translatedError === errorCode ? '' : translatedError;

  const handleException = (
    error: AppState,
    options?: Pick<IHandleExceptionOptions, 'toast' | 'logServiceInstanceMethod' | 'identifier'>,
  ) => {
    setExceptionState({ error, options });
  };

  /**
   * handler function to handle exception
   * - logging
   * - toast/notification display
   * @param error - Error instance
   * @param options - override options to override default options
   */
  const exceptionHandler = () => {
    const { error, options } = exceptionState;
    // logging
    {
      const logServiceMethod = options?.logServiceInstanceMethod ?? defaultOptions?.logServiceInstanceMethod;
      const identifier = options?.identifier ?? defaultOptions?.identifier;

      if (identifier) {
        logServiceMethod?.(identifier, error);
      } else {
        logServiceMethod?.(error);
      }
    }

    // toast logic
    {
      // check for error code in error response
      const toastMessage = error?.responseBody?.code ? translatedError : '';

      if (options?.toast?.enabled ?? defaultOptions?.toast?.enabled) {
        const toastParams = {
          addToast,
          deleteToast,
          message: toastMessage,
          id: '',
          theme: Theme.WARNING as NotificationTheme,
          ...defaultOptions?.toast?.params,
          ...options?.toast?.params,
        };

        // handling fallback error message
        toastParams['message'] ||=
          options?.toast?.fallbackMessage ?? defaultOptions?.toast?.fallbackMessage ?? error?.responseBody?.detail;

        if (toastParams?.message) {
          toastParams['id'] ||= `${uniqueId(16)}`;
          showToastMsg(toastParams);
        }
      }
    }
  };

  useEffect(exceptionHandler, [exceptionState]);

  return { handleException };
}
